import React, { useEffect } from "react";
import { DownloadIcon, InstallIcon } from "../icons/icons";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import SmartwatchHeroMockup from "./SmartwatchHeroMockup";

// Import translations
import fa from "./locales/DownloadHeroSection-fa.json";
import en from "./locales/DownloadHeroSection-en.json";
import ar from "./locales/DownloadHeroSection-ar.json";
import tr from "./locales/DownloadHeroSection-tr.json";

interface HeroSectionProps {
    downloadClick?: () => void;
    setupClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ downloadClick, setupClick }) => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "DownloadHeroSection";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    // تشخیص جهت زبان
    const isRtl = i18n.dir() === "rtl";

    const handleDownloadClick = () => {
        const downloadElement = document.getElementById("app-store-buttons") || document.querySelector(".app-store-buttons") || document.querySelector('[data-section="download"]');

        if (downloadElement) {
            downloadElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.scrollTo({ top: document.body.scrollHeight * 0.3, behavior: "smooth" });
        }
        if (downloadClick) downloadClick();
    };

    const handleSetupClick = () => {
        const setupElement = document.getElementById("InteractiveSetupProcess") || document.querySelector(".setup-process") || document.querySelector('[data-section="setup"]');

        if (setupElement) {
            setupElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.scrollTo({ top: document.body.scrollHeight * 0.6, behavior: "smooth" });
        }
        if (setupClick) setupClick();
    };

    const buttons = [
        {
            text: t("buttons.download", { ns: "DownloadHeroSection" }),
            icon: <DownloadIcon className="w-5 h-5" />,
            className: "bg-gradient-to-r from-[#0038A8] to-[#155DFC] text-white hover:from-[#003090] hover:to-[#104ECC]",
            shadow: "0 10px 25px -5px rgba(0, 56, 168, 0.4)",
            onClick: handleDownloadClick,
        },
        {
            text: t("buttons.setup", { ns: "DownloadHeroSection" }),
            icon: <InstallIcon className="w-5 h-5" />,
            className: "bg-white text-slate-800 border border-slate-200 hover:bg-slate-50",
            shadow: "0 10px 25px -5px rgba(107, 114, 128, 0.15)",
            onClick: handleSetupClick,
        },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative w-full overflow-hidden bg-gradient-to-b from-white via-[#F8FAFC] to-[#F8F9FC] border-b border-[#EAEDF2] pt-24 pb-10 sm:pt-28 sm:pb-14 md:pt-32 md:pb-16 lg:pt-36 lg:pb-20"
        >
            {/* SVG Background Effect */}
            <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden z-0">
                <svg
                    className="w-full h-full object-cover opacity-90"
                    viewBox="0 0 1439 672"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                >
                    <g clipPath="url(#hero_bg_clip0)">
                        <g opacity="0.7" filter="url(#hero_bg_filter0)">
                            <circle cx="1096" cy="482" r="235" fill="url(#hero_bg_paint0)" />
                        </g>
                        <circle opacity="0.1" cx="1096" cy="463" r="235" fill="url(#hero_bg_paint1)" />
                        <g filter="url(#hero_bg_filter1)">
                            <ellipse cx="1067.5" cy="202" rx="14.5" ry="4" fill="url(#hero_bg_paint2)" />
                        </g>
                    </g>
                    <defs>
                        <filter id="hero_bg_filter0" x="341" y="-273" width="1510" height="1510" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                            <feGaussianBlur stdDeviation="260" result="effect1_foregroundBlur_2620_12468" />
                        </filter>
                        <filter id="hero_bg_filter1" x="1037" y="182" width="61" height="40" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                            <feGaussianBlur stdDeviation="8" result="effect1_foregroundBlur_2620_12468" />
                        </filter>
                        <linearGradient id="hero_bg_paint0" x1="1096" y1="247" x2="1096" y2="717" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#0C66E4" />
                            <stop offset="1" stopColor="#579DFF" />
                        </linearGradient>
                        <linearGradient id="hero_bg_paint1" x1="1096" y1="228" x2="1096" y2="698" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#0C66E4" />
                            <stop offset="1" stopColor="#579DFF" />
                        </linearGradient>
                        <linearGradient id="hero_bg_paint2" x1="1067.5" y1="198" x2="1067.5" y2="206" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#0C66E4" />
                            <stop offset="1" stopColor="#579DFF" />
                        </linearGradient>
                        <clipPath id="hero_bg_clip0">
                            <rect width="1439" height="672" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
            </div>

            {/* Content Container */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Content Column (Always on the Left on Desktop) */}
                    <motion.div
                        initial={{ opacity: 0, x: isRtl ? 30 : -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className={`order-1 ${isRtl ? "md:order-2 text-right" : "md:order-1 text-left"} text-center sm:text-start flex flex-col justify-center`}
                    >
                        {/* Main Heading */}
                        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-snug sm:leading-tight text-slate-900 mb-1.5">
                            {t("heading.line1", { ns: "DownloadHeroSection" })}
                        </h1>
                        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-semibold leading-snug sm:leading-tight text-slate-800 mb-1.5">
                            {t("heading.line2", { ns: "DownloadHeroSection" })}
                        </h2>
                

                        {/* Subtitle */}
                        <p className="text-base sm:text-lg text-slate-600 my-6 max-w-xl leading-relaxed">
                            {t("subtitle", { ns: "DownloadHeroSection" })}
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-3 sm:gap-4 mt-2">
                            {buttons.map((btn, idx) => (
                                <motion.button
                                    key={idx}
                                    whileHover={{
                                        scale: 1.03,
                                        boxShadow: btn.shadow,
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={btn.onClick}
                                    className={`w-full sm:w-fit text-sm sm:text-base flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 ${btn.className}`}
                                >
                                    {btn.icon}
                                    <span>{btn.text}</span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>

                    {/* Visual Mockup Column (Always on the Right on Desktop, and below on mobile) */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, x: isRtl ? -30 : 30 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className={`order-2 ${isRtl ? "md:order-1 justify-center lg:justify-start" : "md:order-2 justify-center lg:justify-end"} flex items-center relative w-full`}
                    >
                        <SmartwatchHeroMockup isRtl={isRtl} />
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};

export default HeroSection;
